import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Award, Download, FileText, Image as ImageIcon, Loader2, Lock, ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  { number: 1, title: 'Understanding Cultural Fluency', is_free: true },
  { number: 2, title: 'Building Trust Across Cultures', is_free: false },
  { number: 3, title: 'Leading with Cultural Clarity', is_free: false },
  { number: 4, title: 'Turning Diversity Into Advantage', is_free: false },
];

const MyCertificates = () => {
  const { user, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !isAuthenticated()) {
      navigate('/signin');
      return;
    }
    fetchCompletions();
  }, [isAuthenticated, navigate]);

  const fetchCompletions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/modules/completions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCompletions(data.completions || []);
      }
    } catch (e) {
      console.error('Failed to load completions:', e);
    }
    setLoading(false);
  };

  const handleDownload = async (moduleNumber, format) => {
    setDownloading(`${moduleNumber}-${format}`);
    try {
      const res = await fetch(`${API_URL}/api/modules/${moduleNumber}/certificate/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TCL-Certificate-Module-${moduleNumber}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download error:', e);
    }
    setDownloading(null);
  };

  const isCompleted = (moduleNumber) => completions.some(c => c.module_number === moduleNumber);
  const getCompletion = (moduleNumber) => completions.find(c => c.module_number === moduleNumber);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const completedCount = completions.length;
  const totalModules = MODULES.length;

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      {/* Hero */}
      <section className="bg-[#071B3A] py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
            <Award size={32} className="text-[#C99316]" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mb-3" data-testid="certificates-title">
            My Certificates
          </h1>
          <p className="text-white/60 max-w-xl mx-auto">
            Track your progress and download certificates for completed modules.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Summary */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="progress-summary">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[#071B3A]">Course Progress</h2>
            <span className="text-sm text-slate-500">
              {completedCount} of {totalModules} modules completed
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-[#C99316] to-[#d4b85a] h-3 rounded-full transition-all duration-500"
              style={{ width: `${(completedCount / totalModules) * 100}%` }}
              data-testid="progress-bar"
            />
          </div>
          {completedCount === totalModules && (
            <div className="mt-4 flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3">
              <CheckCircle size={18} />
              <span className="text-sm font-medium">Congratulations! You've completed all modules.</span>
            </div>
          )}
        </div>

        {/* Certificates Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-slate-400" size={32} />
          </div>
        ) : (
          <div className="space-y-4" data-testid="certificates-list">
            {MODULES.map((module) => {
              const completed = isCompleted(module.number);
              const completion = getCompletion(module.number);

              return (
                <div
                  key={module.number}
                  className={`bg-white rounded-xl border p-6 transition-all ${
                    completed
                      ? 'border-[#C99316]/30 shadow-sm'
                      : 'border-slate-200 opacity-70'
                  }`}
                  data-testid={`certificate-card-${module.number}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        completed ? 'bg-[#C99316]/10' : 'bg-slate-100'
                      }`}>
                        {completed ? (
                          <Award size={24} className="text-[#C99316]" />
                        ) : (
                          <Lock size={20} className="text-slate-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                          Module {module.number}
                        </p>
                        <h3 className="font-semibold text-[#071B3A] mb-1">
                          {module.title}
                        </h3>
                        {completed ? (
                          <p className="text-xs text-green-600">
                            Completed on {formatDate(completion.completed_at)}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400">
                            {module.is_free ? 'Free module — complete to earn certificate' : 'Complete this module to earn your certificate'}
                          </p>
                        )}
                      </div>
                    </div>

                    {completed ? (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleDownload(module.number, 'pdf')}
                          disabled={downloading === `${module.number}-pdf`}
                          data-testid={`download-pdf-${module.number}`}
                          className="flex items-center gap-1.5 px-3 py-2 bg-[#071B3A] text-white text-xs font-medium rounded-lg hover:bg-[#071B3A]/90 transition-colors disabled:opacity-50"
                        >
                          {downloading === `${module.number}-pdf` ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <FileText size={14} />
                          )}
                          PDF
                        </button>
                        <button
                          onClick={() => handleDownload(module.number, 'jpg')}
                          disabled={downloading === `${module.number}-jpg`}
                          data-testid={`download-jpg-${module.number}`}
                          className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                        >
                          {downloading === `${module.number}-jpg` ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <ImageIcon size={14} />
                          )}
                          JPG
                        </button>
                      </div>
                    ) : (
                      <Link
                        to="/courses"
                        className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors flex-shrink-0"
                      >
                        Go to Course
                        <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Help text */}
        <div className="mt-10 bg-slate-50 rounded-xl p-6 border border-slate-100">
          <h3 className="font-semibold text-[#071B3A] mb-2 text-sm">How certificates work</h3>
          <ul className="text-sm text-slate-600 space-y-1.5">
            <li>Complete each module by finishing all lessons and marking it as complete.</li>
            <li>Once complete, your personalised certificate is available in PDF and JPG format.</li>
            <li>Certificates include your name, the module title, and completion date.</li>
            <li>Download and share on LinkedIn or add to your professional portfolio.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MyCertificates;
